import i18next from 'i18next';
import { tableSortMap } from 'infrastructure/antd/functions/create-sort-order';
import { useAppDispatch } from 'store';

import { renderPatientCell } from '../patient-cell';
import DueDateCell from '../due-date-cell';
import NoteCell from '../note-cell';
import NotePreview from '../note-preview';
import { buildPatientInfo } from '../utils/patient-info';
import { formatDateCell } from '../utils/table-utils';
import s from './styles.module.scss';

import type { IBaseTableColumns } from 'components/atoms/base-table/types';
import type {
  IKeyNoteData,
  INewlyTableData,
  INewlyTableDataFilters,
} from 'infrastructure/interfaces';

interface Props {
  filter: INewlyTableDataFilters;
}

export const useColumns = ({
  filter,
}: Props): IBaseTableColumns<INewlyTableData> => {
  const {
    ccmConsole: { setTableNote, toggleOpenDrawer },
  } = useAppDispatch();

  return [
    {
      label: i18next.t('labels.patient'),
      key: 'name',
      width: '25%',
      align: 'left',
      dataCy: 'newly-enrolled-patients-name',
      sortable: true,
      sortKey: 'name',
      sortOrder: filter['n-name'] ? tableSortMap[filter['n-name']] : undefined,
      render(_name: string, record) {
        return renderPatientCell({ record });
      },
    },
    {
      label: i18next.t('labels.provider'),
      key: 'provider',
      width: '15%',
      align: 'left',
      dataCy: 'newly-enrolled-patients-provider',
      sortable: true,
      sortOrder: filter['n-provider']
        ? tableSortMap[filter['n-provider']]
        : undefined,
      render(provider: string) {
        return provider;
      },
    },
    {
      label: i18next.t('labels.enrolledDate'),
      key: 'enrolledDate',
      align: 'left',
      dataCy: 'newly-enrolled-patients-enrolled-date',
      sortable: true,
      sortOrder: filter['n-enrolledDate']
        ? tableSortMap[filter['n-enrolledDate']]
        : undefined,
      render(enrolledDate: string) {
        return formatDateCell(enrolledDate);
      },
    },
    {
      label: i18next.t('labels.dueDate'),
      key: 'dueDate',
      align: 'left',
      dataCy: 'newly-enrolled-patients-due-date',
      sortable: true,
      sortOrder: filter['n-dueDate']
        ? tableSortMap[filter['n-dueDate']]
        : undefined,
      render(dueDate: string | null, record) {
        return <DueDateCell date={dueDate} patientGuid={record.guid} />;
      },
    },
    {
      label: i18next.t('labels.lastCall'),
      key: 'lastCallDate',
      align: 'left',
      dataCy: 'newly-enrolled-patients-last-call-date',
      sortable: false,
      sortOrder: filter['n-lastCallDate']
        ? tableSortMap[filter['n-lastCallDate']]
        : undefined,
      render(lastCallDate: string) {
        return formatDateCell(lastCallDate);
      },
    },
    {
      label: i18next.t('labels.ccmAppt'),
      key: 'appointmentDate',
      align: 'left',
      dataCy: 'newly-enrolled-patients-appointment-date',
      sortable: true,
      sortOrder: filter['n-appointmentDate']
        ? tableSortMap[filter['n-appointmentDate']]
        : undefined,
      render(appointmentDate: string) {
        return formatDateCell(appointmentDate);
      },
    },
    {
      label: i18next.t('labels.callsSinceEnrolled'),
      key: 'calls',
      width: '5%',
      align: 'left',
      dataCy: 'newly-enrolled-patients-calls',
      sortable: true,
      sortOrder: filter['n-calls']
        ? tableSortMap[filter['n-calls']]
        : undefined,
      render(calls: string) {
        if (!calls) return '0';
        const parsed = Number(calls);
        if (Number.isNaN(parsed)) return calls;
        return parsed.toString();
      },
    },
    {
      label: i18next.t('labels.notes'),
      key: 'notes',
      width: '10%',
      align: 'left',
      render(notes: IKeyNoteData[] = []) {
        return <NotePreview notes={notes} />;
      },
    },
    {
      label: '',
      key: 'noteaction',
      width: '3%',
      align: 'left',
      render(_unused: IKeyNoteData[] = [], record) {
        const noteList = record.notes ?? [];
        return (
          <NoteCell
            length={noteList.length}
            onClick={() => {
              setTableNote({
                notes: noteList,
                tableName: 'newly',
                patientGuid: record.guid,
                patientName: record.name,
                patientInfo: buildPatientInfo(record),
              });
              toggleOpenDrawer();
            }}
          />
        );
      },
      tableCollClassName: s['note-cell'],
      dataCy: 'newly-enrolled-patients-notes',
    },
  ];
};
